import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  GraduationCap, 
  Play,
  Clock, 
  CheckCircle, 
  Download,
  FileText,
  ArrowLeft,
  Loader2,
  Video
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CourseViewer = () => {
  const { courseId } = useParams();
  const { token } = useAuth();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCourse();
  }, [courseId, token]);

  const fetchCourse = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/learning/courses/${courseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCourse(response.data);
    } catch (error) {
      toast.error('Failed to load course');
    } finally {
      setLoading(false);
    }
  };

  const updateProgress = async (percent) => {
    try {
      await axios.post(`${API_URL}/api/learning/progress/${courseId}?progress_percent=${percent}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCourse(prev => ({ 
        ...prev, 
        progress: percent,
        completed: percent >= 100
      }));
      if (percent >= 100) {
        toast.success('Course completed!');
      }
    } catch (error) {
      toast.error('Failed to update progress');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
        </div>
      </DashboardLayout>
    );
  }

  if (!course) {
    return (
      <DashboardLayout>
        <div className="p-6 lg:p-8 text-center">
          <GraduationCap className="w-12 h-12 text-navy-200 mx-auto mb-4" />
          <h2 className="font-heading text-xl font-semibold text-navy-700 mb-2">Course not found</h2>
          <Link to="/learning">
            <Button variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Courses
            </Button>
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8" data-testid="course-viewer-page">
        {/* Back Button */}
        <Link to="/learning" className="inline-flex items-center gap-2 text-navy-600 hover:text-amber-600 mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          Back to Learning Suite
        </Link>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Video Player */}
            <Card className="border-navy-100 overflow-hidden">
              <div className="aspect-video bg-navy-900 relative flex items-center justify-center">
                {course.video_url ? (
                  <iframe
                    src={course.video_url.replace('watch?v=', 'embed/')}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title={course.title}
                  />
                ) : (
                  <div className="text-center text-white">
                    <Video className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p className="text-lg font-medium">Video Coming Soon</p>
                    <p className="text-sm text-navy-300 mt-1">This course content is being prepared</p>
                  </div>
                )}
              </div>
            </Card>

            {/* Course Info */}
            <Card className="border-navy-100">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <Badge variant="outline" className="mb-2">{course.category}</Badge>
                    <h1 className="font-heading text-2xl font-bold text-navy-900 mb-2">
                      {course.title}
                    </h1>
                    <div className="flex items-center gap-4 text-sm text-navy-500">
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {course.duration_minutes} minutes
                      </span>
                      {course.completed && (
                        <Badge className="bg-emerald-100 text-emerald-700">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Completed
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
                <p className="text-navy-600 leading-relaxed">
                  {course.description}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Progress Card */}
            <Card className="border-navy-100">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <GraduationCap className="w-5 h-5 text-amber-600" />
                  Your Progress
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-navy-600">Progress</span>
                    <span className="font-medium text-navy-900">{course.progress || 0}%</span>
                  </div>
                  <Progress value={course.progress || 0} className="h-3" />
                </div>
                
                {!course.completed && (
                  <div className="space-y-2">
                    <Button 
                      className="w-full bg-amber-600 hover:bg-amber-700"
                      onClick={() => updateProgress(100)}
                      data-testid="complete-course-btn"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Mark as Complete
                    </Button>
                    {course.progress < 50 && (
                      <Button 
                        variant="outline"
                        className="w-full"
                        onClick={() => updateProgress(50)}
                      >
                        Mark 50% Complete
                      </Button>
                    )}
                  </div>
                )}

                {course.completed && (
                  <div className="text-center py-4">
                    <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-2" />
                    <p className="font-medium text-emerald-700">Course Completed!</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Resources Card */}
            {course.resources && course.resources.length > 0 && (
              <Card className="border-navy-100">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <FileText className="w-5 h-5 text-amber-600" />
                    Resources
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {course.resources.map((resource, idx) => (
                      <div 
                        key={idx}
                        className="flex items-center justify-between p-3 bg-navy-50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-white rounded flex items-center justify-center">
                            <FileText className="w-4 h-4 text-amber-600" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-navy-900">{resource.name}</p>
                            <p className="text-xs text-navy-500 uppercase">{resource.type}</p>
                          </div>
                        </div>
                        <Button variant="ghost" size="icon" className="text-navy-400 hover:text-amber-600">
                          <Download className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default CourseViewer;
